import { supabase } from '../lib/supabase';
import { getFriendsCount } from './friendsService';

/** UUID z listy w .env (np. REACT_APP_BADGE_BETA_USER_IDS=id1,id2) — na czas przed migracją flag w DB. */
function userIdSetFromEnv(varName) {
  const raw = process.env[varName];
  if (!raw || typeof raw !== 'string') return new Set();
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
  );
}

/**
 * Statystyki aktywności użytkownika (wydarzenia, znajomi, miejsca) — używane na profilu i w popupie profilu.
 * @param {string} userId
 * @returns {Promise<object>}
 */
export async function fetchUserActivityStats(userId) {
  const empty = {
    participations: { upcoming: 0, completed: 0 },
    organized: 0,
    friendsCount: 0,
    favoriteCategory: null,
    uniqueCities: 0,
    completedEvents: 0,
    organizedEvents: 0,
    isBetaTester: false,
    preLaunchSubscriber: false,
    supporterDonor: false,
  };

  if (!userId) {
    return empty;
  }

  try {
    const today = new Date().toISOString().split('T')[0];

    const { data: participations } = await supabase
      .from('event_participants')
      .select(`
        id,
        events!inner (
          event_date,
          event_type,
          location
        )
      `)
      .eq('user_id', userId)
      .eq('status', 'active');

    const upcoming = participations?.filter((p) => p.events && p.events.event_date >= today) || [];
    const completed = participations?.filter((p) => p.events && p.events.event_date < today) || [];

    const categoryCount = {};
    participations?.forEach((p) => {
      if (p.events?.event_type) {
        categoryCount[p.events.event_type] = (categoryCount[p.events.event_type] || 0) + 1;
      }
    });
    const favoriteCategory = Object.entries(categoryCount)
      .sort((a, b) => b[1] - a[1])[0]?.[0] || null;

    const uniqueCities = new Set(
      participations?.map((p) => p.events?.location).filter(Boolean)
    ).size;

    const { count: organizedCount } = await supabase
      .from('events')
      .select('*', { count: 'exact', head: true })
      .eq('host_id', userId);

    const friendsCountResult = await getFriendsCount(userId);
    const friendsCount = friendsCountResult.success ? (friendsCountResult.count || 0) : 0;

    const uid = String(userId).toLowerCase();
    const betaEnv = userIdSetFromEnv('REACT_APP_BADGE_BETA_USER_IDS');
    const prelaunchEnv = userIdSetFromEnv('REACT_APP_BADGE_PRELAUNCH_USER_IDS');
    const donorEnv = userIdSetFromEnv('REACT_APP_BADGE_DONOR_USER_IDS');

    let isBetaTester = betaEnv.has(uid);
    let preLaunchSubscriber = prelaunchEnv.has(uid);
    let supporterDonor = donorEnv.has(uid);

    try {
      const { data: badgeRow, error: badgeErr } = await supabase
        .from('users')
        .select('is_beta_tester, pre_launch_subscriber, is_supporter_donor')
        .eq('id', userId)
        .maybeSingle();

      if (!badgeErr && badgeRow) {
        isBetaTester = Boolean(isBetaTester || badgeRow.is_beta_tester);
        preLaunchSubscriber = Boolean(preLaunchSubscriber || badgeRow.pre_launch_subscriber);
        supporterDonor = Boolean(supporterDonor || badgeRow.is_supporter_donor);
      }
    } catch (_) {
      /* kolumny mogą nie istnieć — zostają env / false */
    }

    return {
      participations: {
        upcoming: upcoming.length,
        completed: completed.length,
      },
      organized: organizedCount || 0,
      friendsCount,
      favoriteCategory,
      uniqueCities,
      completedEvents: completed.length,
      organizedEvents: organizedCount || 0,
      isBetaTester,
      preLaunchSubscriber,
      supporterDonor,
    };
  } catch (err) {
    console.error('fetchUserActivityStats:', err);
    return empty;
  }
}
